import { memo, useMemo } from 'react';
import * as Yup from 'yup';
import { Formik, FormikHelpers } from 'formik';

import { CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { CreateInterviewBody } from '@synergetx/puppet-interview-types';

import { DialogSubmitButtons } from './DialogSubmitButtons';
import CreateInterviewInput from './CreateInterviewInput';
import { createNewInterview } from '@modules/api';
import { userMessage } from '@modules/api-error';
import { logError } from '@modules/logging';
import { toast } from 'react-toastify';
import { useInterviewOptions } from '@providers/InterviewsProvider';

interface Props {
    onClose: () => void;
    groupId: string;
    baseInterviewId?: string;
    baseGroupId?: string;
}

const addInterviewValidationSchema = Yup.object<CreateInterviewBody>({
    name: Yup.string().label('Name').required('Name is required').min(1, 'Name is required'),
    groupId: Yup.string()
        .label('Group Id')
        .required('Group ID is required')
        .min(1, 'Group ID is required'),

    interviewToDuplicate: Yup.object(),
});

const AddInterviewDialog = ({
    onClose,
    groupId = '',
    baseInterviewId = '',
    baseGroupId = '',
}: Props) => {
    const { addAndSelectNewInterview } = useInterviewOptions();
    const initialValues: CreateInterviewBody = useMemo(
        () => ({
            groupId,
            name: '',
            baseInterviewId: baseInterviewId || '',
            baseGroupId: baseGroupId || '',
        }),
        [baseGroupId, baseInterviewId, groupId],
    );

    const onSubmit = async (
        values: CreateInterviewBody,
        { setSubmitting }: FormikHelpers<CreateInterviewBody>,
    ) => {
        setSubmitting(true);

        try {
            const { interview } = await toast.promise(createNewInterview(values), {
                error: {
                    render: ({ data }) =>
                        userMessage(data, 'Error creating new survey. Please try again.'),
                },
                pending: `Creating new survey...`,
                success: 'Success! Survey created!',
            });

            onClose();
            if (interview.id) addAndSelectNewInterview.current(interview);
        } catch (error) {
            // Leave the dialog open with the entered name so it can be retried.
            logError(error as Error);
        } finally {
            // Previously skipped on failure, which left the dialog stuck on its spinner.
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            enableReinitialize
            validateOnChange={true}
            validationSchema={addInterviewValidationSchema}
        >
            {({ errors, dirty, submitForm, values, isSubmitting }) => (
                <Dialog maxWidth="md" fullWidth open onClose={onClose}>
                    {/* <Dialog maxWidth="md" fullWidth open onClose={() => setOpen(undefined)}> */}
                    <DialogTitle>{`Add New Survey to ${values.groupId}`}</DialogTitle>
                    {isSubmitting ? (
                        <CircularProgress style={{ alignSelf: 'center', margin: '5rem' }} />
                    ) : (
                        <>
                            <DialogContent dividers>
                                <CreateInterviewInput />
                            </DialogContent>
                            <DialogSubmitButtons
                                submitDisabled={!dirty || Object.keys(errors).length > 0}
                                onSubmit={submitForm}
                                onClose={onClose}
                            />
                        </>
                    )}
                </Dialog>
            )}
        </Formik>
    );
};

export default memo(AddInterviewDialog);
